import { TextInput, TextInputProps } from '@primer/react';
import { useState, useEffect } from 'react';

export interface INumberInputProps extends Omit<TextInputProps, 'type' | 'onChange'> {
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  min?: number;
  max?: number;
}

const NumberInput = (props: INumberInputProps) => {
  const { value, onChange, readOnly, min, max, ...restProps } = props;
  const [displayValue, setDisplayValue] = useState<string>('');

  useEffect(() => {
    if (value !== undefined && value !== null) {
      const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      setDisplayValue(numValue.toLocaleString('vi-VN'));
    } else {
      setDisplayValue('0');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Remove all non-digit characters
    const cleanedValue = inputValue.replace(/[^\d]/g, '');
    
    if (cleanedValue === '') {
      setDisplayValue('0');
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: '0' }
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
      return;
    }

    let numValue = parseInt(cleanedValue, 10);
    
    // Apply min/max constraints
    if (min !== undefined && numValue < min) {
      numValue = min;
    }
    if (max !== undefined && numValue > max) {
      numValue = max;
    }
    
    const formattedValue = numValue.toLocaleString('vi-VN');
    setDisplayValue(formattedValue);

    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: String(numValue) }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (value !== undefined && value !== null) {
      const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      setDisplayValue(numValue.toLocaleString('vi-VN'));
    }
  };

  return (
    <TextInput
      {...restProps}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      readOnly={readOnly}
      sx={{
        textAlign: 'right',
        '& input': {
          textAlign: 'right'
        },
        ...(restProps.sx || {})
      }}
    />
  );
};

export default NumberInput;

